/**
 * The Visit Types grid's data: one row per task type an agency can create,
 * each clinic's auto-create list, and the edits waiting for Save.
 *
 * Billable is agency-wide and Create on admission is per clinic; both wait for
 * the page's Save. Alias changes save at once.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../../store';
import {
  archiveTaskTypeAlias,
  createTaskTypeAlias,
  getTaskTypes,
  renameTaskTypeAlias,
  updateTaskTypes,
} from '../../../services/taskTypesApi';
import { getAgencyClinics } from '../../../services/clinicsApi';
import { updateAgencyClinicAgencyClinicsAgencyClinicIdPut as updateAgencyClinic } from '../../../services/api/sdk.gen';
import type {
  AgencyClinicResponse,
  AutoCreateTaskKind,
  AutoCreateTaskType,
  DisciplineType,
  TaskTypeRowResponse,
  TaskTypesResponse,
  VisitTypeId,
} from '../../../services/api/types.gen';
import {
  TASK_TYPE_DISCIPLINE_MAP,
  type TaskTypeId,
  isOrderType,
} from '../../../constants/visitTypes';
import {
  clearVisitTypeOptionsCache,
  optionTypeId,
  useVisitTypeOptions,
} from '../../../hooks/useVisitTypeOptions';
import { createCreatableTaskTypeOptions } from '../../../components/task/taskTypeFilters';
import { getBusinessErrorMessage } from '../../../utils/apiErrorDetail';
import {
  ALL_CLINICS,
  type Resolved,
  resolveAcrossClinics,
} from './clinicScope';

export type VisitTypesRow = {
  /** `kind:type`, the key an auto-create entry is matched by. */
  id: string;
  kind: AutoCreateTaskKind;
  type: string;
  name: string;
  discipline: DisciplineType | null;
  /** Scheduling Points' grouping for a visit; the kind for anything else. */
  category: string;
  /** The Visit Types API's row, for a visit the user may read billing on. */
  taskTypeRow?: TaskTypeRowResponse;
};

const rowId = (kind: string, type: string) => `${kind}:${type}`;
const cellKey = (clinicId: string, id: string) => `${clinicId}|${id}`;

/** Whether a clinic's list holds the row, under any alias. */
const listHas = (list: AutoCreateTaskType[], row: VisitTypesRow) =>
  list.some(entry => entry.kind === row.kind && entry.type === row.type);

const CLINIC_PAGE = 100;

/** Every clinic, a page at a time: All clinics must reach each one. */
async function loadEveryClinic(
  agencyId: string
): Promise<AgencyClinicResponse[]> {
  const clinics: AgencyClinicResponse[] = [];
  for (;;) {
    const page = await getAgencyClinics(agencyId, clinics.length, CLINIC_PAGE);
    clinics.push(...page);
    if (page.length < CLINIC_PAGE) return clinics;
  }
}

export function useVisitTypesData({
  scope,
  canReadVisitTypes,
  canReadAutoCreate,
}: {
  scope: string;
  canReadVisitTypes: boolean;
  canReadAutoCreate: boolean;
}) {
  const { agencyId, showSuccess, showError } = useStore(
    useShallow(state => ({
      agencyId: state.user?.agency?.id,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );
  const { options: visitTypeOptions, loading: optionsLoading } =
    useVisitTypeOptions();

  const [taskTypeRows, setTaskTypeRows] = useState<
    TaskTypeRowResponse[] | null
  >(null);
  const [clinics, setClinics] = useState<AgencyClinicResponse[] | null>(null);
  const [savedLists, setSavedLists] = useState<
    Record<string, AutoCreateTaskType[]>
  >({});
  const [loadFailed, setLoadFailed] = useState(false);
  // Billable by visit type, and Create on admission by clinic and row.
  const [pendingBillable, setPendingBillable] = useState<
    Record<string, boolean>
  >({});
  const [pendingAuto, setPendingAuto] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [aliasBusy, setAliasBusy] = useState(false);

  useEffect(() => {
    if (!canReadVisitTypes) return;
    let cancelled = false;
    getTaskTypes({ throwOnError: true })
      .then(result => {
        if (!result.data)
          throw new Error('Task types response carried no data');
        if (!cancelled) setTaskTypeRows(result.data.rows);
      })
      .catch(error => {
        console.error('Failed to load visit types', error);
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [canReadVisitTypes]);

  useEffect(() => {
    if (!canReadAutoCreate || !agencyId) return;
    let cancelled = false;
    loadEveryClinic(agencyId)
      .then(data => {
        if (cancelled) return;
        setClinics(data);
        setSavedLists(
          Object.fromEntries(
            data.map(clinic => [clinic.id, clinic.auto_create_task_types ?? []])
          )
        );
      })
      .catch(error => {
        console.error('Failed to load patient creation settings', error);
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [canReadAutoCreate, agencyId]);

  const rows = useMemo<VisitTypesRow[]>(() => {
    const byId = new Map<string, VisitTypesRow>();
    for (const row of taskTypeRows ?? []) {
      const id = rowId('visit', row.visit_type);
      byId.set(id, {
        id,
        kind: 'visit',
        type: row.visit_type,
        name: row.display_name,
        discipline: row.discipline,
        category: row.category ?? 'routine',
        taskTypeRow: row,
      });
    }
    // The Add Task picker's types: anything the agency can create by hand.
    // An alias option is not a row of its own.
    for (const option of createCreatableTaskTypeOptions(visitTypeOptions)) {
      if (option.taskKind === 'communication' || option.taskKind === 'misc') {
        const kind = option.taskKind;
        const id = rowId(kind, option.id);
        if (!byId.has(id))
          byId.set(id, {
            id,
            kind,
            type: option.id,
            name: option.display_name,
            discipline: null,
            category: kind,
          });
        continue;
      }
      if (option.alias_id) continue;
      const typeId = optionTypeId(option);
      const kind = isOrderType(typeId) ? 'order' : 'visit';
      const id = rowId(kind, typeId);
      if (byId.has(id)) continue;
      byId.set(id, {
        id,
        kind,
        type: typeId,
        name: option.display_name,
        discipline:
          kind === 'visit'
            ? (TASK_TYPE_DISCIPLINE_MAP[typeId as TaskTypeId] ?? null)
            : null,
        category: kind === 'order' ? 'order' : 'visit',
      });
    }
    return [...byId.values()];
  }, [taskTypeRows, visitTypeOptions]);

  const clinicsInScope = useMemo(
    () =>
      (clinics ?? []).filter(
        clinic => scope === ALL_CLINICS || clinic.id === scope
      ),
    [clinics, scope]
  );
  const clinicName = useCallback(
    (id: string) =>
      clinics?.find(clinic => clinic.id === id)?.name || 'Unnamed clinic',
    [clinics]
  );

  /** Null for a row with no billing answer (anything but a visit). */
  const billableFor = useCallback(
    (row: VisitTypesRow): boolean | null => {
      if (!row.taskTypeRow) return null;
      return pendingBillable[row.type] ?? !row.taskTypeRow.non_billable;
    },
    [pendingBillable]
  );

  const saveBillable = useCallback((row: VisitTypesRow, value: boolean) => {
    if (!row.taskTypeRow) return;
    const original = !row.taskTypeRow.non_billable;
    setPendingBillable(current => {
      const next = { ...current };
      if (value === original) delete next[row.type];
      else next[row.type] = value;
      return next;
    });
  }, []);

  const autoCreateFor = useCallback(
    (row: VisitTypesRow): Resolved<boolean> =>
      resolveAcrossClinics(
        clinicsInScope.map(clinic => [
          clinic.id,
          pendingAuto[cellKey(clinic.id, row.id)] ??
            listHas(savedLists[clinic.id] ?? [], row),
        ]),
        clinicName
      ),
    [clinicsInScope, pendingAuto, savedLists, clinicName]
  );

  /** Stages the value for every clinic in scope. */
  const setAutoCreated = useCallback(
    (row: VisitTypesRow, value: boolean) => {
      setPendingAuto(current => {
        const next = { ...current };
        for (const clinic of clinicsInScope) {
          const key = cellKey(clinic.id, row.id);
          if (value === listHas(savedLists[clinic.id] ?? [], row))
            delete next[key];
          else next[key] = value;
        }
        return next;
      });
    },
    [clinicsInScope, savedLists]
  );

  const isPending = useCallback(
    (row: VisitTypesRow, column: 'billable' | 'auto') =>
      column === 'billable'
        ? row.type in pendingBillable && !!row.taskTypeRow
        : clinicsInScope.some(
            clinic => cellKey(clinic.id, row.id) in pendingAuto
          ),
    [pendingBillable, pendingAuto, clinicsInScope]
  );

  // One per cell the admin changed: a tick under All clinics counts once.
  const pendingCount =
    Object.keys(pendingBillable).length +
    new Set(Object.keys(pendingAuto).map(key => key.split('|')[1])).size;

  const revert = useCallback(() => {
    setPendingBillable({});
    setPendingAuto({});
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (pendingCount === 0) return true;
    setSaving(true);
    const byClinic = new Map<string, [string, boolean][]>();
    for (const [key, value] of Object.entries(pendingAuto)) {
      const [clinicId, id] = key.split('|');
      byClinic.set(clinicId, [...(byClinic.get(clinicId) ?? []), [id, value]]);
    }
    const rowsById = new Map(rows.map(row => [row.id, row]));

    // Back to Roger's answer withdraws the agency's, so Roger's later changes
    // reach the row again.
    const billableEntries = Object.entries(pendingBillable);
    const isRogerAnswer = ([visitType, billable]: [string, boolean]) =>
      rowsById.get(rowId('visit', visitType))?.taskTypeRow
        ?.roger_default_non_billable === !billable;
    const billableSave = billableEntries.length
      ? updateTaskTypes({
          body: {
            changes: billableEntries
              .filter(entry => !isRogerAnswer(entry))
              .map(([visitType, billable]) => ({
                visit_type: visitType as VisitTypeId,
                non_billable: !billable,
              })),
            resets: billableEntries
              .filter(isRogerAnswer)
              .map(([visitType]) => visitType as VisitTypeId),
          },
          throwOnError: true,
        }).then(result => {
          if (!result.data)
            throw new Error('Task types response carried no data');
          setTaskTypeRows(result.data.rows);
          setPendingBillable({});
          // The create-task pickers cache each type's billability for the session.
          clearVisitTypeOptionsCache();
        })
      : null;

    // Each clinic's whole list: entries the grid does not show (an alias the
    // agency picked) ride along unchanged, unless their row is turned off.
    const clinicSaves = [...byClinic.entries()].map(([clinicId, changes]) => {
      let list = savedLists[clinicId] ?? [];
      for (const [id, on] of changes) {
        const row = rowsById.get(id);
        if (!row) continue;
        if (!on) {
          list = list.filter(
            entry => !(entry.kind === row.kind && entry.type === row.type)
          );
        } else if (!listHas(list, row)) {
          list = [...list, { kind: row.kind, type: row.type, alias_id: null }];
        }
      }
      return updateAgencyClinic({
        path: { agency_clinic_id: clinicId },
        body: { auto_create_task_types: list },
        throwOnError: true,
      }).then(result => {
        setSavedLists(current => ({
          ...current,
          [clinicId]: result.data?.auto_create_task_types ?? list,
        }));
        setPendingAuto(current =>
          Object.fromEntries(
            Object.entries(current).filter(
              ([key]) => !key.startsWith(`${clinicId}|`)
            )
          )
        );
      });
    });

    const results = await Promise.allSettled([
      ...(billableSave ? [billableSave] : []),
      ...clinicSaves,
    ]);
    setSaving(false);
    const failures = results.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );
    if (failures.length === 0) {
      showSuccess('Visit types saved');
      return true;
    }
    failures.forEach(failure =>
      console.error('Failed to save visit types', failure.reason)
    );
    showError(
      getBusinessErrorMessage(failures[0].reason) ??
        'Some changes did not save. They are still marked unsaved.'
    );
    return false;
  }, [
    pendingCount,
    pendingAuto,
    pendingBillable,
    rows,
    savedLists,
    showError,
    showSuccess,
  ]);

  // Alias writes return the whole Visit Types grid; unsaved ticks survive.
  const runAliasWrite = useCallback(
    async (
      write: () => Promise<{ data?: TaskTypesResponse }>,
      success: string,
      failure: string
    ): Promise<boolean> => {
      setAliasBusy(true);
      try {
        const result = await write();
        if (!result.data)
          throw new Error('Visit types response carried no data');
        setTaskTypeRows(result.data.rows);
        // The create-task pickers cache the alias list for the session.
        clearVisitTypeOptionsCache();
        showSuccess(success);
        return true;
      } catch (error) {
        console.error(failure, error);
        showError(getBusinessErrorMessage(error) ?? failure);
        return false;
      } finally {
        setAliasBusy(false);
      }
    },
    [showError, showSuccess]
  );

  const addAlias = useCallback(
    (row: VisitTypesRow, name: string) =>
      runAliasWrite(
        () =>
          createTaskTypeAlias({
            body: { visit_type: row.type as VisitTypeId, name },
            throwOnError: true,
          }),
        'Alias added',
        'Failed to add alias'
      ),
    [runAliasWrite]
  );
  const renameAlias = useCallback(
    (aliasId: string, name: string) =>
      runAliasWrite(
        () =>
          renameTaskTypeAlias({
            path: { alias_id: aliasId },
            body: { name },
            throwOnError: true,
          }),
        'Alias renamed',
        'Failed to rename alias'
      ),
    [runAliasWrite]
  );
  const archiveAlias = useCallback(
    (aliasId: string) =>
      runAliasWrite(
        () =>
          archiveTaskTypeAlias({
            path: { alias_id: aliasId },
            throwOnError: true,
          }),
        'Alias removed',
        'Failed to remove alias'
      ),
    [runAliasWrite]
  );

  const loading =
    !loadFailed &&
    (optionsLoading ||
      (canReadVisitTypes && taskTypeRows === null) ||
      (canReadAutoCreate && !!agencyId && clinics === null));

  return {
    rows,
    clinics: clinics ?? [],
    loading,
    loadFailed,
    saving,
    aliasBusy,
    pendingCount,
    billableFor,
    saveBillable,
    autoCreateFor,
    setAutoCreated,
    isPending,
    save,
    revert,
    addAlias,
    renameAlias,
    archiveAlias,
  };
}

export type VisitTypesData = ReturnType<typeof useVisitTypesData>;
